import { ref } from 'vue'
import { platformFetch } from './platform/http.ts'
import { isAndroidRuntime, isTauriRuntime } from './platform/runtime.ts'
import { getPlatformString, readPlatformStringSync, setPlatformString } from './platform/storage.ts'

const STORAGE_KEY = 'azusa.backend.base'
export const BROWSER_PROXY_BACKEND_BASE = '/v1'
export const DESKTOP_BACKEND_BASE = 'http://127.0.0.1:8080/v1'
export const ANDROID_EMULATOR_BACKEND_BASE = 'http://127.0.0.1:8080/v1'
export const LAN_BACKEND_EXAMPLE = 'http://192.168.1.10:8080/v1'
export const DEFAULT_BACKEND_BASE = defaultBackendBase()

export interface BackendPreset {
  id: string
  label: string
  value: string
  description: string
}

export interface BackendConnectionTestResult {
  ok: boolean
  url: string
  status?: number
  message: string
}

function runtimeDefaultBackendBase(): string {
  if (!isTauriRuntime()) return BROWSER_PROXY_BACKEND_BASE
  if (isAndroidRuntime()) return ANDROID_EMULATOR_BACKEND_BASE
  return DESKTOP_BACKEND_BASE
}

function configuredBackendBase(): string {
  const environment = import.meta.env as ImportMetaEnv | undefined
  const raw = environment?.VITE_AZUSA_BACKEND_BASE || environment?.VITE_API_BASE || ''
  return raw.trim() ? normalizeBackendBase(raw, runtimeDefaultBackendBase()) : ''
}

function defaultBackendBase(): string {
  return configuredBackendBase() || runtimeDefaultBackendBase()
}

export function normalizeBackendBase(raw: string, fallback = DEFAULT_BACKEND_BASE): string {
  const trimmed = raw.trim()
  if (!trimmed) return fallback

  if (trimmed.startsWith('/')) {
    if (requiresAbsoluteNativeBackendBase(trimmed)) return fallback
    return trimmed === '/' ? fallback : trimmed.replace(/\/+$/, '')
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed.replace(/\/+$/, '')
  }

  return `http://${trimmed.replace(/\/+$/, '')}`
}

export function requiresAbsoluteNativeBackendBase(raw: string): boolean {
  return isTauriRuntime() && raw.trim().startsWith('/')
}

export function backendConnectionPresets(): BackendPreset[] {
  const presets: BackendPreset[] = []
  const configured = configuredBackendBase()
  if (configured) {
    presets.push({
      id: 'configured',
      label: 'Build default',
      value: configured,
      description: 'From VITE_AZUSA_BACKEND_BASE.',
    })
  }

  if (!isTauriRuntime()) {
    presets.push({
      id: 'browser-proxy',
      label: 'Browser proxy',
      value: BROWSER_PROXY_BACKEND_BASE,
      description: 'Use the Vite or same-origin /v1 proxy.',
    })
  }

  if (isAndroidRuntime()) {
    presets.push({
      id: 'android-emulator',
      label: 'Android emulator',
      value: ANDROID_EMULATOR_BACKEND_BASE,
      description: 'Reach core through adb reverse on the emulator.',
    })
    presets.push({
      id: 'android-lan',
      label: 'Android device',
      value: LAN_BACKEND_EXAMPLE,
      description: 'Edit to your computer LAN IP before saving.',
    })
  } else {
    presets.push({
      id: 'desktop-local',
      label: 'Local core',
      value: DESKTOP_BACKEND_BASE,
      description: 'Core running on this computer.',
    })
  }

  presets.push({
    id: 'remote-https',
    label: 'HTTPS remote',
    value: 'https://example.com/v1',
    description: 'Edit to a deployed or tunneled core URL.',
  })

  return dedupePresets(presets)
}

function readStoredBackendBase(): string {
  const stored = readPlatformStringSync(STORAGE_KEY)
  return normalizeBackendBase(stored || DEFAULT_BACKEND_BASE)
}

export const backendBase = ref<string>(readStoredBackendBase())

export function activateBackendBase(next: string): string {
  const normalized = normalizeBackendBase(next)
  backendBase.value = normalized
  return normalized
}

export async function hydrateBackendBase(): Promise<string> {
  const stored = await getPlatformString(STORAGE_KEY)
  const normalized = normalizeBackendBase(stored || DEFAULT_BACKEND_BASE)
  backendBase.value = normalized
  return normalized
}

export async function setBackendBase(next: string) {
  const normalized = normalizeBackendBase(next)
  await setPlatformString(STORAGE_KEY, normalized)
  backendBase.value = normalized
  return normalized
}

export function resetBackendBase() {
  return setBackendBase(DEFAULT_BACKEND_BASE)
}

export function apiPath(path: string): string {
  return backendPathForBase(backendBase.value, path)
}

export function websocketPath(path: string): string {
  const suffix = path.startsWith('/') ? path : `/${path}`
  const base = backendBase.value

  if (base.startsWith('/')) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${protocol}//${window.location.host}${base}${suffix}`
  }

  const url = new URL(base)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  url.pathname = `${url.pathname.replace(/\/+$/, '')}${suffix}`
  url.search = ''
  url.hash = ''
  return url.toString()
}

export function backendPathForBase(base: string, path: string): string {
  const suffix = path.startsWith('/') ? path : `/${path}`
  return `${normalizeBackendBase(base)}${suffix}`
}

export async function testBackendBase(base: string): Promise<BackendConnectionTestResult> {
  if (requiresAbsoluteNativeBackendBase(base)) {
    return {
      ok: false,
      url: base.trim(),
      message: 'Native apps need an absolute http(s) backend URL.',
    }
  }

  const url = withCacheBust(backendPathForBase(base, '/healthz'))
  try {
    const response = await platformFetch(url, {
      cache: 'no-store',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
    if ((response.status >= 200 && response.status < 300) || response.status === 304) {
      return { ok: true, url, status: response.status, message: 'Backend is reachable.' }
    }
    return {
      ok: false,
      url,
      status: response.status,
      message: `Backend responded with HTTP ${response.status}.`,
    }
  } catch (err) {
    return {
      ok: false,
      url,
      message: err instanceof Error ? err.message : 'Backend connection failed.',
    }
  }
}

function withCacheBust(url: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}_ts=${Date.now()}`
}

function dedupePresets(presets: BackendPreset[]): BackendPreset[] {
  const seen = new Set<string>()
  return presets.filter((preset) => {
    const key = normalizeBackendBase(preset.value)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}
